import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../../auth/AuthContext'
import type {
    CreateProspectoKanbanColumnaRequest,
    CreateProspectoKanbanRequest,
    ProspectoKanbanCardDTO,
    ProspectoKanbanColumnaDTO,
    ProspectoKanbanDTO,
    WrapperResponse,
} from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import { parseDate, shiftIso, todayIso } from '../../../utils/dates'
import { clip } from '../../../utils/inputs'
import * as Permisos from '../../../utils/Permisos'
import {
    byOrden,
    cardRequest,
    COLUMN_NAME_MAX,
    DEFAULT_BOARD_NAME,
    errorMessage,
    formatCompactDate,
    isMoral,
    isVisibleStatus,
    money,
    opportunityStatusView,
    personName,
    statusQuery,
} from '../common/opportunity-common'

const LOAD_ERROR = 'No se pudo cargar el tablero de prospección.'
const MOVE_ERROR = 'No se pudo mover la tarjeta; se recargó el tablero.'
const COLUMN_ERROR = 'No se pudo guardar la columna.'
const DELETE_COLUMN_ERROR = 'No se pudo eliminar la columna.'
const DELETE_CARD_ERROR = 'No se pudo eliminar la oportunidad.'

export interface OpportunityBoardFilters {
    startDate: string
    endDate: string
    showClosed: boolean
}

export interface OpportunityCardView {
    id: number
    nombre: string
    prospecto: string
    moral: boolean
    descripcion: string
    status: { label: string; className: string } | null
    prima: string
    ramo: string | null
    dias: string
    fecha: string
}

export interface OpportunityColumnView {
    id: number
    nombre: string
    count: number
    totales: string
    cards: OpportunityCardView[]
}

type DragState =
    | { type: 'card'; cardId: number; fromColumnId: number }
    | { type: 'column'; columnId: number }

export interface OpportunityDrawerState {
    cardId: number | null
    columnId: number | null
}

const defaultFilters = (): OpportunityBoardFilters => ({ startDate: shiftIso(-60), endDate: todayIso(), showClosed: false })

const normalizeColumns = (columns?: ProspectoKanbanColumnaDTO[] | null) =>
    byOrden(columns).map((column) => ({ ...column, cards: byOrden(column.cards) }))

const totalsText = (cards: ProspectoKanbanCardDTO[]) => {
    const totals = new Map<string, number>()
    cards.forEach((card) => {
        const code = card.moneda?.abreviacion || 'MXN'
        totals.set(code, (totals.get(code) || 0) + Number(card.primaTotal || 0))
    })
    if (totals.size === 0) return money(0, 'MXN', 0)
    return Array.from(totals.entries()).map(([code, total]) => `${money(total, code, 0)} ${code}`).join(' · ')
}

const toCardView = (card: ProspectoKanbanCardDTO): OpportunityCardView => {
    const status = opportunityStatusView(card.status)
    const dias = Number(card.dias ?? 0)
    return {
        id: card.id,
        nombre: card.nombre || 'Sin nombre',
        prospecto: card.prospecto ? personName(card.prospecto) : 'Sin prospecto',
        moral: isMoral(card.prospecto?.fiscal),
        descripcion: card.descripcion || '',
        status: status.id === 'abierta' ? null : status,
        prima: money(card.primaTotal, card.moneda?.abreviacion || 'MXN', 0),
        ramo: card.ramo?.ramo || null,
        dias: `${dias} ${dias === 1 ? 'día' : 'días'}`,
        fecha: formatCompactDate(card.fecRegistro) || '—',
    }
}

/** Índice final tras quitar el elemento de su posición original (al moverlo dentro de la misma lista). */
const targetIndex = (fromIndex: number, rawIndex: number, sameList: boolean) => (sameList && rawIndex > fromIndex ? rawIndex - 1 : rawIndex)

export const useOpportunityBoardModel = () => {
    const { canAccess } = useAuth()
    const toastTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
    const [filters, setFilters] = useState<OpportunityBoardFilters>(defaultFilters)
    const [kanbanId, setKanbanId] = useState<number | null>(null)
    const [columns, setColumns] = useState<ProspectoKanbanColumnaDTO[]>([])
    const [loading, setLoading] = useState(true)
    const [initialLoadDone, setInitialLoadDone] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [reloadToken, setReloadToken] = useState(0)
    const [toast, setToast] = useState<string | null>(null)
    const [drag, setDrag] = useState<DragState | null>(null)
    const [cardHint, setCardHint] = useState<{ columnId: number; index: number } | null>(null)
    const [columnHint, setColumnHint] = useState<number | null>(null)
    const [drawer, setDrawer] = useState<OpportunityDrawerState | null>(null)
    const [addingColumn, setAddingColumn] = useState(false)
    const [columnName, setColumnName] = useState('')
    const [savingColumn, setSavingColumn] = useState(false)
    const [confirmColumnId, setConfirmColumnId] = useState<number | null>(null)
    const [confirmCardId, setConfirmCardId] = useState<number | null>(null)

    const canWrite = canAccess(Permisos.PROSPECCION_W)
    const rangeInvalid = Boolean(filters.startDate && filters.endDate && (parseDate(filters.startDate)?.toMillis() ?? 0) > (parseDate(filters.endDate)?.toMillis() ?? 0))

    const showToast = (message: string) => {
        clearTimeout(toastTimer.current)
        setToast(message)
        toastTimer.current = setTimeout(() => setToast(null), 3000)
    }

    useEffect(() => () => clearTimeout(toastTimer.current), [])

    useEffect(() => {
        if (!filters.startDate || !filters.endDate || rangeInvalid) return undefined
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                let id = kanbanId
                if (!id) {
                    const listPayload: WrapperResponse<ProspectoKanbanDTO[]> | null = await (await APIInvoke.invokeGET('/prospectos/kanbans')).json()
                    if (!listPayload?.ok) throw new Error(listPayload?.message || LOAD_ERROR)
                    id = listPayload.body?.[0]?.id ?? null
                    if (!id) {
                        const request: CreateProspectoKanbanRequest = { nombre: DEFAULT_BOARD_NAME }
                        const created: WrapperResponse<ProspectoKanbanDTO> | null = await (await APIInvoke.invokePOST('/prospectos/kanbans', request)).json()
                        if (!created?.ok || !created.body) throw new Error(created?.message || LOAD_ERROR)
                        id = created.body.id
                    }
                }
                const query = `status=${statusQuery(filters.showClosed)}&startDate=${filters.startDate}&endDate=${filters.endDate}`
                const payload: WrapperResponse<ProspectoKanbanDTO> | null = await (await APIInvoke.invokeGET(`/prospectos/kanbans/${id}?${query}`)).json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || LOAD_ERROR)
                setKanbanId(id)
                setColumns(normalizeColumns(payload.body.columns))
                setInitialLoadDone(true)
            } catch (loadError) {
                if (active) setError(errorMessage(loadError, LOAD_ERROR))
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, reloadToken])

    const reload = () => setReloadToken((token) => token + 1)

    const findCard = (cardId: number) => {
        for (const column of columns) {
            const index = (column.cards || []).findIndex((card) => card.id === cardId)
            if (index >= 0) return { column, index, card: (column.cards || [])[index] }
        }
        return null
    }

    const moveCard = async (cardId: number, toColumnId: number, rawIndex: number) => {
        const found = findCard(cardId)
        if (!found) return
        const sameColumn = found.column.id === toColumnId
        const index = targetIndex(found.index, rawIndex, sameColumn)
        if (sameColumn && index === found.index) return
        const moved = { ...found.card, columnId: toColumnId }
        setColumns((current) => {
            const withoutCard = current.map((column) => ({ ...column, cards: (column.cards || []).filter((card) => card.id !== cardId) }))
            return withoutCard.map((column) => {
                if (column.id !== toColumnId) return column
                const cards = [...(column.cards || [])]
                cards.splice(Math.min(index, cards.length), 0, moved)
                return { ...column, cards }
            })
        })
        try {
            const payload: WrapperResponse<ProspectoKanbanCardDTO> | null = await (
                await APIInvoke.invokePUT(`/prospectos/kanbans/cards/${cardId}`, cardRequest(found.card, { columnId: toColumnId, orden: index }))
            ).json()
            if (!payload?.ok) throw new Error(payload?.message || MOVE_ERROR)
        } catch {
            showToast(MOVE_ERROR)
            reload()
        }
    }

    const moveColumn = async (columnId: number, rawIndex: number) => {
        const fromIndex = columns.findIndex((column) => column.id === columnId)
        if (fromIndex < 0 || !kanbanId) return
        const index = targetIndex(fromIndex, rawIndex, true)
        if (index === fromIndex) return
        const column = columns[fromIndex]
        setColumns((current) => {
            const next = current.filter((item) => item.id !== columnId)
            next.splice(index, 0, column)
            return next
        })
        try {
            const request: CreateProspectoKanbanColumnaRequest = { kanbanId, nombre: column.nombre || '', orden: index }
            const payload: WrapperResponse<ProspectoKanbanColumnaDTO> | null = await (await APIInvoke.invokePUT(`/prospectos/kanbans/columnas/${columnId}`, request)).json()
            if (!payload?.ok) throw new Error(payload?.message || COLUMN_ERROR)
        } catch {
            showToast(COLUMN_ERROR)
            reload()
        }
    }

    const endDrag = () => {
        setDrag(null)
        setCardHint(null)
        setColumnHint(null)
    }

    const dnd = {
        startCard: (cardId: number, fromColumnId: number) => {
            if (canWrite) setDrag({ type: 'card', cardId, fromColumnId })
        },
        startColumn: (columnId: number) => {
            if (canWrite) setDrag({ type: 'column', columnId })
        },
        /** `index` es la posición dentro de la columna antes de quitar la tarjeta arrastrada. */
        overCard: (columnId: number, index: number) => {
            if (drag?.type === 'card') setCardHint({ columnId, index })
        },
        overColumn: (columnId: number, columnIndex: number, after: boolean) => {
            if (drag?.type === 'column') setColumnHint(after ? columnIndex + 1 : columnIndex)
            if (drag?.type === 'card' && cardHint?.columnId !== columnId) {
                const column = columns.find((item) => item.id === columnId)
                setCardHint({ columnId, index: column?.cards?.length || 0 })
            }
        },
        drop: () => {
            if (drag?.type === 'card' && cardHint) moveCard(drag.cardId, cardHint.columnId, cardHint.index)
            if (drag?.type === 'column' && columnHint != null) moveColumn(drag.columnId, columnHint)
            endDrag()
        },
        end: endDrag,
    }

    const addColumn = async () => {
        const nombre = columnName.trim()
        if (!nombre || !kanbanId || savingColumn) return
        try {
            setSavingColumn(true)
            const request: CreateProspectoKanbanColumnaRequest = { kanbanId, nombre }
            const payload: WrapperResponse<ProspectoKanbanColumnaDTO> | null = await (await APIInvoke.invokePOST('/prospectos/kanbans/columnas', request)).json()
            if (!payload?.ok || !payload.body) throw new Error(payload?.message || COLUMN_ERROR)
            const created = payload.body
            setColumns((current) => [...current, { ...created, cards: created.cards || [] }])
            setColumnName('')
            setAddingColumn(false)
        } catch (saveError) {
            showToast(errorMessage(saveError, COLUMN_ERROR))
        } finally {
            setSavingColumn(false)
        }
    }

    const deleteColumn = async () => {
        const columnId = confirmColumnId
        setConfirmColumnId(null)
        if (!columnId) return
        try {
            const payload: WrapperResponse<unknown> | null = await (await APIInvoke.invokeDELETE(`/prospectos/kanbans/columnas/${columnId}`)).json()
            if (!payload?.ok) throw new Error(payload?.message || DELETE_COLUMN_ERROR)
            setColumns((current) => current.filter((column) => column.id !== columnId))
            showToast('Columna eliminada')
        } catch (deleteError) {
            showToast(errorMessage(deleteError, DELETE_COLUMN_ERROR))
        }
    }

    const deleteCard = async () => {
        const cardId = confirmCardId
        setConfirmCardId(null)
        if (!cardId) return
        try {
            const payload: WrapperResponse<unknown> | null = await (await APIInvoke.invokeDELETE(`/prospectos/kanbans/cards/${cardId}`)).json()
            if (!payload?.ok) throw new Error(payload?.message || DELETE_CARD_ERROR)
            setColumns((current) => current.map((column) => ({ ...column, cards: (column.cards || []).filter((card) => card.id !== cardId) })))
            if (drawer?.cardId === cardId) setDrawer(null)
            showToast('Oportunidad eliminada')
        } catch (deleteError) {
            showToast(errorMessage(deleteError, DELETE_CARD_ERROR))
        }
    }

    /** Refleja en el tablero una tarjeta creada o editada desde el drawer. */
    const cardSaved = (saved: ProspectoKanbanCardDTO) => {
        setColumns((current) => {
            let previous: { columnId: number; index: number } | null = null
            const withoutCard = current.map((column) => {
                const index = (column.cards || []).findIndex((card) => card.id === saved.id)
                if (index >= 0) previous = { columnId: column.id, index }
                return index >= 0 ? { ...column, cards: (column.cards || []).filter((card) => card.id !== saved.id) } : column
            })
            if (!isVisibleStatus(saved.status, filters.showClosed)) return withoutCard
            const keep = previous as { columnId: number; index: number } | null
            return withoutCard.map((column) => {
                if (column.id !== saved.columnId) return column
                const cards = [...(column.cards || [])]
                cards.splice(keep && keep.columnId === column.id ? keep.index : 0, 0, saved)
                return { ...column, cards }
            })
        })
        setDrawer((current) => (current ? { cardId: saved.id, columnId: saved.columnId } : current))
    }

    const columnViews: OpportunityColumnView[] = columns.map((column) => {
        const cards = column.cards || []
        return {
            id: column.id,
            nombre: column.nombre || 'Sin nombre',
            count: cards.length,
            totales: totalsText(cards),
            cards: cards.map(toCardView),
        }
    })

    const openCount = columns.reduce((total, column) => total + (column.cards || []).filter((card) => card.status === 'abierta').length, 0)

    return {
        filters,
        rangeInvalid,
        loading,
        initialLoadDone,
        error,
        toast,
        canWrite,
        columns: columnViews,
        openCount,
        columnOptions: columns.map((column) => ({ id: column.id, nombre: column.nombre || 'Sin nombre' })),
        dragging: drag,
        cardHint,
        columnHint,
        drawer,
        addingColumn,
        columnName,
        savingColumn,
        confirmColumn: columnViews.find((column) => column.id === confirmColumnId) || null,
        confirmCard: confirmCardId ? findCard(confirmCardId)?.card || null : null,
        changeStartDate: (startDate: string) => setFilters((current) => ({ ...current, startDate })),
        changeEndDate: (endDate: string) => setFilters((current) => ({ ...current, endDate })),
        changeShowClosed: (showClosed: boolean) => setFilters((current) => ({ ...current, showClosed })),
        clearFilters: () => setFilters(defaultFilters()),
        reload,
        dnd,
        openCard: (cardId: number, columnId: number) => setDrawer({ cardId, columnId }),
        newCard: (columnId: number) => setDrawer({ cardId: null, columnId }),
        closeDrawer: () => setDrawer(null),
        cardSaved,
        startAddColumn: () => setAddingColumn(true),
        cancelAddColumn: () => {
            setAddingColumn(false)
            setColumnName('')
        },
        changeColumnName: (value: string) => setColumnName(clip(value, COLUMN_NAME_MAX)),
        addColumn,
        askDeleteColumn: (columnId: number) => setConfirmColumnId(columnId),
        cancelDeleteColumn: () => setConfirmColumnId(null),
        deleteColumn,
        askDeleteCard: (cardId: number) => setConfirmCardId(cardId),
        cancelDeleteCard: () => setConfirmCardId(null),
        deleteCard,
    }
}
